import { RegistrationSource } from "@/types/types";
import { CANONICAL_LEAD_SOURCES, normalizeLeadSourceKey } from "./leadSources";

const REGISTRATION_SOURCE_VALUES = new Set<string>(Object.values(RegistrationSource));
const CANONICAL_VALUES = new Set(CANONICAL_LEAD_SOURCES.map((s) => s.value));

/**
 * Map CRM `Lead.source` → patient registration form value (+ optional details).
 * Prefers canonical source keys (e.g. META_PAID) when listed; otherwise maps to RegistrationSource enum.
 */
export function mapCrmLeadSourceToRegistration(
	leadSource: string | null | undefined
): {
	registrationSource: string;
	registrationSourceDetails: string;
} {
	const raw = String(leadSource ?? "").trim();
	const canonical = normalizeLeadSourceKey(raw);
	if (!canonical) {
		return {
			registrationSource: RegistrationSource.CUSTOM,
			registrationSourceDetails: "CRM converted lead",
		};
	}

	if (CANONICAL_VALUES.has(canonical)) {
		const needsDetails =
			canonical === "REFERRAL" ||
			canonical === "AFFILIATE" ||
			canonical === "CUSTOM" ||
			canonical === "OTHER";
		return {
			registrationSource: canonical,
			registrationSourceDetails: needsDetails ? raw : "",
		};
	}

	if (REGISTRATION_SOURCE_VALUES.has(canonical)) {
		return {
			registrationSource: canonical,
			registrationSourceDetails: "",
		};
	}

	const aliases: Record<string, RegistrationSource> = {
		WALKIN: RegistrationSource.WALK_IN,
		META_PAID: RegistrationSource.META,
		WHATSAPP_INSTA_ADS: RegistrationSource.META,
		INSTAGRAM_ORGANIC: RegistrationSource.META,
		WHATSAPP: RegistrationSource.META,
		GOOGLE_PAID: RegistrationSource.GOOGLE,
		ORGANIC_GOOGLE: RegistrationSource.GOOGLE,
		GOOGLE_MY_BUSINESS: RegistrationSource.GMB,
		GOOGLEMYBUSINESS: RegistrationSource.GMB,
		OFFLINE: RegistrationSource.OFFLINE_MARKETING,
		DIRECT_LANDING: RegistrationSource.WEBSITE,
		ORGANIC_DIRECT: RegistrationSource.WEBSITE,
		EXOTEL: RegistrationSource.CUSTOM,
		OTHER: RegistrationSource.CUSTOM,
	};

	const mapped = aliases[canonical];
	if (mapped) {
		return {
			registrationSource: mapped,
			registrationSourceDetails: mapped === canonical ? "" : raw,
		};
	}

	return {
		registrationSource: RegistrationSource.CUSTOM,
		registrationSourceDetails: raw,
	};
}
